import React, { useEffect, useState, Suspense, lazy } from 'react';
import { initAnalytics, track, identifyFromLocalStorage } from "./analytics/mixpanel";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore } from './firebase';
import { setUserData } from './redux/userSlice';
import { doc, getDoc } from 'firebase/firestore';

// Synchronous import for main page to keep LCP fast
import LandingPage from './components/LandingPage';
import Loader from './components/Loader';
import ScrollToTop from './components/ScrollToTop';
import { REPORT_CONFIG } from './constants/reportLandingConfig';
import ReactGA from 'react-ga4';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import './App.css';

// Lazy Loaded Routes
const UserForm = lazy(() => import('./components/UserForm'));
const ChatInterface = lazy(() => import('./components/ChatInterface'));
const Profile = lazy(() => import('./components/Profile'));
const PricingPage = lazy(() => import('./components/PricingPage'));
const PlanManagementPage = lazy(() => import('./components/PlanManagementPage'));
const About = lazy(() => import('./components/About'));
const ReturnAndRefund = lazy(() => import('./components/ReturnAndRefund'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./components/TermsAndConditions'));
const SupportPage = lazy(() => import('./components/SupportPage'));
const FeedbackPage = lazy(() => import('./components/FeedbackPage'));
const Horoscope = lazy(() => import('./components/Horoscope'));
const ReportForm = lazy(() => import('./components/ReportForm'));
const MiniReportsPage = lazy(() => import('./components/MiniReportsPage'));
const ReportDetailPage = lazy(() => import('./components/ReportDetailPage'));
const PersonalizedHoroscope = lazy(() => import('./components/PersonalizedHoroscope'));
const TransitReportPage = lazy(() => import('./components/TransitReportPage'));
const MyStoryPage = lazy(() => import('./components/MyStoryPage'));
const ReportLandingPage = lazy(() => import('./components/ReportLandingPage'));
const KundliMatching = lazy(() => import('./components/KundliMatching'));
const KundliLandingPage = lazy(() => import('./components/KundliLandingPage'));
const AccountDeletion = lazy(() => import('./components/AccountDeletion'));
const LifeReportPage = lazy(() => import('./features/lifeReport/ReportPage'));
const MyChartsPage = lazy(() => import('./features/charts/MyChartsPage'));

const GA_MEASUREMENT_ID = 'G-EHKNT1X608';

const getPlatform = () => {
  if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
    return 'PWA';
  }
  return 'Website';
};

const App = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [loadingData, setLoadingData] = useState(true);
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.userData);

  // Global SEO Check for Staging
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes('staging') || hostname === 'staging.vedicrashi.in') {
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
      console.log('App: Staging environment detected. Added noindex, nofollow.');
    }
  }, []);

  useEffect(() => {
    ReactGA.initialize(GA_MEASUREMENT_ID);
    ReactGA.send("pageview"); // Sends initial page view
    initAnalytics();
    // identify returning users using YOUR localStorage key
    identifyFromLocalStorage("userId"); // e.g., "vr_user_id"
    // this will no-op if the key doesn’t exist yet (new user)
    track("App Opened", { platform: getPlatform() }); // will send only if identified
  }, []);

  useEffect(() => {
    // Helper to serialize Firestore data (convert Timestamps to millis)
    const serializeData = (data) => {
      if (!data) return null;
      const serialized = { ...data };
      Object.keys(serialized).forEach(key => {
        const value = serialized[key];
        if (value && typeof value === 'object' && value.seconds) {
          // It's likely a Firestore Timestamp
          serialized[key] = value.toMillis ? value.toMillis() : value.seconds * 1000;
        }
      });
      return serialized;
    };

    const checkUserProfile = async (uid) => {
      try {
        const userRef = doc(firestore, 'users', uid);
        const docSnap = await getDoc(userRef);

        if (docSnap.exists()) {
          const rawData = docSnap.data();
          const serializedData = serializeData(rawData);
          dispatch(setUserData(serializedData));
        } else {
          dispatch(setUserData(null));
        }
      } catch (error) {
        console.error("Error fetching user data: ", error);
        dispatch(setUserData(null));
      } finally {
        setLoadingData(false);
      }
    };

    if (user) {
      checkUserProfile(user.uid);
    } else {
      setLoadingData(false); // No user, so we're done loading data
    }
  }, [user, dispatch]);

  // Only block the entire app render if we think there's a logged-in user
  // For new users (no userId in local storage), let the app render immediately!
  const hasLocalUser = localStorage.getItem('userId');
  if ((loadingAuth || loadingData) && hasLocalUser) {
    return <Loader />;
  }

  // FormRoute only checks userData fields
  const FormRoute = ({ children }) => {
    // Check if user is logged in AND has complete profile
    if (user && userData && userData.dob && userData.time && userData.location) {
      return <Navigate to="/chat?login=complete" />;
    }
    return children;
  };

  // ⚠️ MAINTENANCE MODE - REMOVE AFTER 2 PM IST
  const MAINTENANCE_MODE = false;

  if (MAINTENANCE_MODE) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a0f2e 0%, #2d1b4e 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '20px',
        fontFamily: "var(--vr-font-family)"
      }}>
        <h2 style={{ fontSize: '1.5rem', color: '#ffd700', marginBottom: '30px', fontWeight: '700' }}>
          Vedic Rashi
        </h2>
        <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔧</div>
        <h1 style={{ fontSize: '2rem', marginBottom: '10px', fontWeight: '600' }}>
          We're Under Maintenance
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#b8a9c9', maxWidth: '400px', lineHeight: '1.6' }}>
          We're making some improvements to serve you better.
          We'll be back by <strong style={{ color: '#ffd700' }}>2:00 PM IST</strong>.
        </p>
        <p style={{ fontSize: '0.9rem', color: '#8a7a9a', marginTop: '30px' }}>
          Thank you for your patience! ✨
        </p>
      </div>
    );
  }

  return (
    <Router>
      <GA4PageTracker />
      <ToastContainer />
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          {/* Authentication page removed - users login via chat/onboarding flow */}
          <Route path="/form" element={<FormRoute><UserForm /></FormRoute>} />
          <Route path="/chat" element={<ChatInterface />} />

          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/plan-management" element={<PlanManagementPage />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/about" element={<About />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/life-report/*" element={<LifeReportPage />} />
          <Route path="/life-reports" element={<LifeReportPage library />} />
          <Route path="/mini-reports" element={<MiniReportsPage />} />
          <Route path="/mini-report" element={<ReportDetailPage />} />

          {/* Dynamic Report Routes */}
          {Object.values(REPORT_CONFIG).map((config) => (
            <Route
              key={config.slug}
              path={`/report/${config.slug}`}
              element={<ReportLandingPage type={config.slug} />}
            />
          ))}

          <Route path="/report/:reportName/payment-page" element={<ReportForm />} />
          <Route path="/horoscope/:sign/:date/:type" element={<Horoscope />} />
          <Route path="/my-horoscope" element={<PersonalizedHoroscope />} />
          <Route path="/my-chart" element={<MyChartsPage />} />
          <Route path="/transit-report" element={<TransitReportPage />} />
          <Route path="/my-story" element={<MyStoryPage />} />
          <Route path="/kundli-matching" element={<KundliMatching />} />
          <Route path="/kundli-analysis" element={<KundliLandingPage />} />
          <Route path="/returns" element={<ReturnAndRefund />} />
          <Route path="/delete-account" element={<AccountDeletion />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/tnc" element={<TermsAndConditions />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

// Page Tracker Component to Handle Route Changes
const GA4PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search,
    });
    // Mixpanel SPA pageview
    track("Page Viewed", {
      path: location.pathname,
      query: location.search || "",
      title: document.title || "",
    });
  }, [location]);

  return null; // This component doesn't render anything
};

export default App;
